import type { AnalyticsRange, StaffAnalyticsOverview } from "@lafayette/schemas";

import { Notice } from "../../ui/notice";
import { hasNoRecords, rangePhrase } from "../overview-model";
import type { CustomerView } from "../queries";
import { AppActivityCards } from "./AppActivityCards";
import { appDataWords } from "./app-data-words";
import { KpiTiles } from "./KpiTiles";
import {
  ActivityCard,
  FunnelCard,
  RequestTypesCard,
  ServiceCard,
  SpendCard,
} from "./OverviewCards";
import { RecentActivityCard } from "./RecentActivityCard";

// The Overview tab (Paper "Analytics · Customer cockpit"), from RECORDS: the tiles, the activity over time, the request funnel, what the
// customer asks for, the spend and the service numbers. The cards that need data the product does not hold yet (predictions, satisfaction,
// segments, the app sessions) are not drawn. The app cards read the `app` part of the answer (CHT-2029); the recent-visits card reads the events (CHT-2030).

function AppDataNotice({ overview }: Readonly<{ overview: StaffAnalyticsOverview }>) {
  const { availability } = overview.appData;
  // The app cards show real numbers when the app part is there: no "not available" words next to them.
  if (availability === "AVAILABLE" || overview.app !== undefined) return null;
  return (
    <Notice tone={availability === "OPTED_OUT" ? "info" : "neutral"} role="note">
      {appDataWords(availability, overview.access.displayName)}
    </Notice>
  );
}

export function OverviewTab({
  overview,
  range,
  view,
  customerId,
}: Readonly<{
  overview: StaffAnalyticsOverview;
  range: AnalyticsRange;
  view: CustomerView;
  customerId: string;
}>) {
  const { records, access } = overview;
  const empty = hasNoRecords(records);
  return (
    <div className="grid gap-4">
      <AppDataNotice overview={overview} />
      {empty ? (
        <Notice tone="neutral" role="status">
          There is no record for {access.displayName} {rangePhrase(range)}. Try a longer range.
        </Notice>
      ) : null}
      <KpiTiles records={records} range={range} />
      <div className="grid gap-4 lg:grid-cols-3">
        <ActivityCard records={records} tz={access.window.tz} range={range} />
        <FunnelCard records={records} range={range} />
        <RequestTypesCard records={records} name={access.displayName} range={range} />
        <SpendCard records={records} range={range} />
        <ServiceCard records={records} range={range} />
        <AppActivityCards overview={overview} range={range} />
        <RecentActivityCard view={view} customerId={customerId} range={range} access={access} />
      </div>
      <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
        Sources: Lafayette records (requests, payments, conversations, handoffs, trips, saved items
        and notifications). Money is in rupees, paid and not refunded.
      </p>
    </div>
  );
}
